import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Plane, Headphones, User, Menu, X, ChevronDown, Search as SearchIcon } from "lucide-react";
import TranslatePage from "../TranslatePage";
import { useAuth } from "../../context/AuthContext";

/* AirlinesAirports.com site shell — global header, nav and footer.
   Design: white header, navy brand, dropdown nav (per user mockup). */

const NAV = [
  { label: "Airlines", items: [["All Airlines", "/airlines"], ["Directory A–Z", "/airlines/directory"], ["Global Ranking", "/airlines/ranking"]] },
  { label: "Airports", items: [["All Airports", "/airports"], ["Directory A–Z", "/airports/directory"], ["World's Busiest", "/airports/ranking"], ["India Airports", "/india"]] },
  { label: "Countries", to: "/countries" },
  { label: "Routes", to: "/routes" },
  { label: "Flight Status", to: "/my-flight" },
  { label: "Guides", to: "/guides" },
  { label: "Library", to: "/articles" },
  { label: "News", items: [["Aviation News", "/aviation-news"], ["Travel News", "/aviation-news?scope=travel"], ["What Changed Today", "/news/today"], ["By Country & City", "/aviation-news?scope=global"], ["Editorial Library", "/editorial"]] },
  { label: "India", to: "/india" },
];

const SiteSearch = ({ className = "", onDone }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setQ(""); onDone?.();
  };
  return (
    <form onSubmit={submit} role="search" data-testid="aa-site-search"
      className={`items-center gap-1.5 border border-zinc-300 rounded-full bg-zinc-50 focus-within:border-blue-500 focus-within:bg-white px-3 py-1.5 ${className}`}>
      <SearchIcon className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
      <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="aa-site-search-input"
        placeholder="Search anything…" aria-label="Search the site"
        className="bg-transparent outline-none text-[12px] font-600 w-full min-w-0 placeholder:text-zinc-400" />
    </form>
  );
};

export function AAHeader() {
  const [open, setOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const { user } = useAuth();

  const listen = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (synth.speaking) { synth.cancel(); setSpeaking(false); return; }
    const text = document.querySelector("main")?.innerText?.slice(0, 3000) || "";
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    try {
      const p = JSON.parse(localStorage.getItem("tp_tts") || "{}");
      if (p.voiceURI) {
        const v = synth.getVoices().find((x) => x.voiceURI === p.voiceURI);
        if (v) u.voice = v;
      }
      if (p.rate) u.rate = p.rate;
    } catch { /* default voice */ }
    synth.speak(u);
    setSpeaking(true);
  };

  return (
    <header className="border-b border-zinc-200 bg-white sticky top-0 z-40" data-testid="aa-header">
      <div className="max-w-7xl mx-auto px-2 sm:px-5 py-2.5 flex items-center gap-1 sm:gap-4 overflow-x-clip">
        <Link to="/" className="flex items-center gap-1.5 max-[360px]:gap-1 sm:gap-2.5 shrink min-w-0 lg:shrink-0 lg:min-w-fit" data-testid="aa-logo">
          <span className="inline-flex p-1.5 sm:p-2 rounded-full bg-[#0A2A5E] text-white shrink-0"><Plane className="w-4 h-4 sm:w-5 sm:h-5 -rotate-45" /></span>
          <span className="min-w-0">
            <span className="notranslate block truncate font-heading font-900 text-sm max-[360px]:text-[13px] sm:text-xl xl:text-2xl leading-none whitespace-nowrap text-[#0A2A5E]">AirlinesAirports<span className="text-blue-600">.com</span></span>
            <span className="hidden sm:block text-[11px] text-zinc-500 font-600 mt-0.5">Your Journey. Our Information.</span>
          </span>
        </Link>

        {/* Desktop nav moved to its own second row (see below) */}
        <SiteSearch className="hidden lg:flex flex-1 max-w-md ml-auto" />

        <div className="ml-auto lg:ml-0 flex items-center gap-0.5 sm:gap-1.5 text-xs min-w-0 shrink-0">
          <span className="block" data-testid="aa-translate"><TranslatePage /></span>
          <button onClick={listen} data-testid="aa-listen-btn" aria-label={speaking ? "Stop listening" : "Listen to this page"}
            className={`inline-flex items-center gap-1 font-800 px-1 max-[360px]:px-0.5 sm:px-2 py-1.5 rounded-full transition-colors ${speaking ? "text-white bg-blue-700" : "text-zinc-600 hover:text-blue-700"}`}>
            <Headphones className="w-4 h-4" />
            <span className="hidden lg:inline text-xs whitespace-nowrap">{speaking ? "Stop" : "Listen to ▸"}</span>
          </button>
          {user ? (
            <Link to="/travel-hub?src=%2Faa-header" data-testid="aa-account" aria-label="My account"
              className="inline-flex items-center font-700 text-blue-800 px-1 sm:px-1.5 py-1.5">
              <span className="w-6 h-6 rounded-full bg-[#0A2A5E] text-white text-[11px] font-800 inline-flex items-center justify-center">
                {(user.name || user.email || "T")[0].toUpperCase()}
              </span>
            </Link>
          ) : (
            <Link to="/login" data-testid="aa-signin" aria-label="Sign in"
              className="inline-flex items-center gap-1 font-700 text-zinc-600 hover:text-blue-700 px-1 sm:px-1.5 py-1.5">
              <User className="w-4 h-4" /><span className="hidden lg:inline text-xs">Sign In</span>
            </Link>
          )}
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-1 max-[360px]:p-0.5 text-zinc-600 shrink-0" data-testid="aa-nav-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Row 2 — primary navigation (larger, easier to read) */}
      <div className="hidden lg:block border-t border-zinc-100">
        <nav className="max-w-7xl mx-auto px-5 flex items-center gap-1" data-testid="aa-nav">
          {NAV.map((n) => n.items ? (
            <div key={n.label} className="relative group">
              <button data-testid={`aa-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1 text-[15px] font-700 text-zinc-800 hover:text-blue-700 px-3.5 py-2.5 transition-colors">
                {n.label} <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full hidden group-hover:block z-50">
                <div className="bg-white border border-zinc-200 rounded-b-lg shadow-lg py-1.5 min-w-[210px]">
                  {n.items.map(([label, to]) => (
                    <Link key={to} to={to} data-testid={`aa-nav-sub-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="block px-4 py-2.5 text-sm font-600 text-zinc-600 hover:text-blue-700 hover:bg-blue-50 transition-colors">{label}</Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <NavLink key={n.label} to={n.to} data-testid={`aa-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `text-[15px] font-700 px-3.5 py-2.5 transition-colors ${isActive ? "text-blue-700 border-b-2 border-blue-700" : "text-zinc-800 hover:text-blue-700"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav className="lg:hidden border-t border-zinc-100 bg-white" data-testid="aa-nav-mobile">
          <div className="max-w-7xl mx-auto px-5 py-2 flex flex-col">
            <SiteSearch className="flex my-2" onDone={() => setOpen(false)} />
            {NAV.map((n) => n.items ? (
              <div key={n.label} className="py-1">
                <div className="text-[11px] font-800 uppercase tracking-wide text-zinc-400 px-1 pt-2">{n.label}</div>
                {n.items.map(([label, to]) => (
                  <Link key={to} to={to} onClick={() => setOpen(false)}
                    className="block px-3 py-2.5 text-base font-700 text-zinc-700 hover:text-blue-700 rounded-md hover:bg-blue-50">{label}</Link>
                ))}
              </div>
            ) : (
              <NavLink key={n.label} to={n.to} onClick={() => setOpen(false)}
                className="px-1 py-3 text-base font-800 text-zinc-700 hover:text-blue-700 border-b border-zinc-50">{n.label}</NavLink>
            ))}
            <Link to="/travel-hub" onClick={() => setOpen(false)} className="px-1 py-2.5 text-sm font-800 text-blue-800">My Travel Hub</Link>
            <Link to="/get-app" onClick={() => setOpen(false)} className="px-1 py-2.5 text-sm font-800 text-zinc-700">Download App</Link>
          </div>
        </nav>
      )}
    </header>
  );
}

export function AAFooter() {
  return (
    <footer className="border-t border-zinc-200 bg-zinc-50 mt-10" data-testid="aa-footer">
      <div className="max-w-7xl mx-auto px-5 py-8 flex flex-wrap items-start gap-8">
        <div className="max-w-xs">
          <div className="font-heading font-900 text-lg text-[#0A2A5E] notranslate">AirlinesAirports<span className="text-blue-600 text-xs">.com</span></div>
          <p className="text-xs text-zinc-500 mt-1">Your Journey. Our Information. The world's most comprehensive resource for airlines, airports and routes.</p>
          <p className="text-[11px] text-zinc-400 mt-2">Part of the <a href="https://travelerpulse.pro" target="_blank" rel="noopener noreferrer" className="font-700 text-blue-700 hover:underline">TravelerPulse</a> network</p>
        </div>
        <div className="text-xs space-y-1.5">
          <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Explore</div>
          <Link to="/airports" className="block text-zinc-600 hover:text-blue-700">Airports Directory</Link>
          <Link to="/airlines" className="block text-zinc-600 hover:text-blue-700">Airlines Directory</Link>
          <Link to="/aircraft" className="block text-zinc-600 hover:text-blue-700">Aircraft</Link>
          <Link to="/map" className="block text-zinc-600 hover:text-blue-700">Route Map</Link>
          <Link to="/aviation-news" className="block text-zinc-600 hover:text-blue-700">Aviation News</Link>
          <Link to="/india" className="block text-zinc-600 hover:text-blue-700">IndianAirports Platform</Link>
        </div>
        <div className="text-xs space-y-1.5">
          <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Plan</div>
          <Link to="/flight-search" className="block text-zinc-600 hover:text-blue-700">Flight Search</Link>
          <Link to="/plan-ai" className="block text-zinc-600 hover:text-blue-700">AI Trip Planner</Link>
          <Link to="/travel-hub" className="block text-zinc-600 hover:text-blue-700">My Travel Hub</Link>
          <Link to="/get-app" className="block text-zinc-600 hover:text-blue-700">Download App</Link>
        </div>
        <div className="text-xs space-y-1.5">
          <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Trust</div>
          <Link to="/about" className="block text-zinc-600 hover:text-blue-700">About</Link>
          <Link to="/editorial-policy" className="block text-zinc-600 hover:text-blue-700">Editorial Policy</Link>
          <Link to="/corrections-policy" className="block text-zinc-600 hover:text-blue-700">Corrections</Link>
          <Link to="/sources" className="block text-zinc-600 hover:text-blue-700">Sources & Verification</Link>
          <Link to="/ai-disclosure" className="block text-zinc-600 hover:text-blue-700">AI Disclosure</Link>
          <Link to="/cookie-policy" className="block text-zinc-600 hover:text-blue-700">Cookie Policy</Link>
          <Link to="/media-rights" className="block text-zinc-600 hover:text-blue-700">Media Rights</Link>
          <Link to="/advertising-policy" className="block text-zinc-600 hover:text-blue-700">Advertising</Link>
          <Link to="/legal/privacy" className="block text-zinc-600 hover:text-blue-700">Privacy</Link>
          <Link to="/legal/terms" className="block text-zinc-600 hover:text-blue-700">Terms</Link>
        </div>
        <p className="text-[11px] text-zinc-400 ml-auto max-w-[240px]">Aviation data changes — every record carries a source and last-verified date. Always confirm critical details with your airline.</p>
      </div>
    </footer>
  );
}
